import { defineStore } from "pinia";
import {
    FetchState,
    NotificationPreferencesState,
    NotificationPreferencesResponse,
    NotificationPreferenceUpdateRequest,
    NotificationPreference,
    NotificationPreferenceType,
    Preference,
    CommunicationDeliveryType,
} from "@/shared/types";
import {
    getNotificationPreferences,
    updateNotificationPreferences,
} from "@/shared/api/notificationPreferencesService";
import * as storeHelper from "@/shared/storeHelper";
import _, { update } from "lodash";
import { useNotifierStore } from "./notifier";

export const initialState: NotificationPreferencesState = {
    notificationPreferencesFetchState: FetchState.Uninitialized,
    notificationPreferences: [],
    notificationPreferencesSnapshot: [],
    hasSavedNotificationPreferences: false,
    updatedPreferences: [],
};

export const useNotificationPreferencesStore = defineStore("notificationPreferences", {
    state: (): NotificationPreferencesState => (_.cloneDeep(initialState)),
    getters: {
        notificationPreferencesLoading: state => state.notificationPreferencesFetchState === FetchState.Loading,
        notificationPreferencesLoaded: state => state.notificationPreferencesFetchState === FetchState.Loaded,
        notificationPreferencesList: state => state.notificationPreferences,
        HasAnyPaperPreferencesOn (): boolean {
            for (const notificationPreference of this.notificationPreferences) {
                for (const preference of notificationPreference.preferences) {
                    if (preference.configurations.find(x =>
                        x.communicationDeliveryType === CommunicationDeliveryType.Paper &&
                        x.optedIn === true &&
                        x.enabled === true)) {
                        return true;
                    }
                }
            }
            return false;
        },
    },
    actions: {
        setNotificationPreferences (response: NotificationPreferencesResponse) {
            this.notificationPreferences = this.normalizePreferences(response.notificationPreferences);
            this.notificationPreferencesSnapshot = _.cloneDeep(this.notificationPreferences);
        },
        setNotificationPreferencesFetchState (fetchState: FetchState) {
            this.notificationPreferencesFetchState = fetchState;
        },
        hasThresholdOption (preference: Preference): boolean {
            return !!(preference && (preference.notificationPreferenceType === NotificationPreferenceType.HsaWithdrawalWarning ||
                preference.notificationPreferenceType === NotificationPreferenceType.HsaCashBalanceWarning ||
                preference.notificationPreferenceType === NotificationPreferenceType.HsaCashMaximumBalanceWarning));
        },
        normalizePreferences (notificationPreferences: NotificationPreference[]): NotificationPreference[] {
            return notificationPreferences.map(notification => {
                return {
                    ...notification,
                    preferences: notification.preferences.map(pref => {
                        const hasThresholdOption = this.hasThresholdOption(pref);
                        if (!hasThresholdOption) {
                            return pref;
                        }
                        if (!pref.threshold) {
                            return {
                                ...pref,
                                threshold: {
                                    value: ""
                                }
                            };
                        }
                        if (pref.threshold && typeof pref.threshold === "object") {
                            const threshold = pref.threshold as { value?: number | null };

                            return {
                                ...pref,
                                threshold: {
                                    ...threshold,
                                    value: threshold.value ?? ""
                                }
                            };
                        }
                        return pref;
                    })
                };
            });
        },
        updatePaperlessCommunicationPreferences () {
            for (const notificationPreference of this.notificationPreferences) {
                for (const [prefIndex, preference] of notificationPreference.preferences.entries()) {
                    for (const configuration of preference.configurations) {
                        if (configuration.communicationDeliveryType === CommunicationDeliveryType.Paper &&
                            configuration.optedIn === true &&
                            configuration.enabled === true) {
                            configuration.optedIn = false;
                            const matchingPreference = notificationPreference.preferences[prefIndex];
                            for (const matchingConfiguration of matchingPreference.configurations) {
                                if (matchingConfiguration.communicationDeliveryType === CommunicationDeliveryType.Email &&
                                    matchingConfiguration.optedIn === false &&
                                    matchingConfiguration.enabled === true) {
                                    matchingConfiguration.optedIn = true;
                                }
                            }
                        }
                    }
                }
            }
        },
        AddChangedNotificationPreferences () {
            for (const notification of this.notificationPreferences) {
                const existingNotificationIndex = this.updatedPreferences.findIndex(
                    n => n.category === notification.category
                );

                for (const preference of notification.preferences) {
                    const snapshotNotification =
                        this.notificationPreferencesSnapshot.find(
                            x => x.category === notification.category
                        );

                    const snapshotPreference = snapshotNotification?.preferences.find(
                        x =>
                            x.notificationPreferenceType ===
                            preference.notificationPreferenceType
                    );

                    const hasPreferenceChanged = !_.isEqual(preference, snapshotPreference);
                    if (!hasPreferenceChanged) {
                        continue;
                    }

                    if (preference.threshold && preference.threshold.value > 0) {
                        if (!this.hasPaperlessCommunications(preference)) {
                            continue;
                        }
                    }

                    const thresholdCopy = preference.threshold
                        ? {value: preference.threshold.value}
                        : null;

                    const changedPreference = {
                        notificationPreferenceType: preference.notificationPreferenceType,
                        name: preference.name,
                        configurations: preference.configurations.map(c => ({
                            communicationDeliveryType: c.communicationDeliveryType,
                            enabled: c.enabled,
                            optedIn: c.optedIn
                        })),
                        threshold: thresholdCopy
                    };

                    if (existingNotificationIndex === -1) {
                        this.updatedPreferences.push({
                            category: notification.category,
                            categoryDescription: notification.categoryDescription,
                            preferences: [changedPreference]
                        });
                    } else {
                        const existingNotification = this.updatedPreferences[existingNotificationIndex];
                        const existingPreferenceIndex =
                            existingNotification.preferences.findIndex(
                                x =>
                                    x.notificationPreferenceType ===
                                    preference.notificationPreferenceType
                            );

                        if (existingPreferenceIndex === -1) {
                            existingNotification.preferences.push(changedPreference);
                        } else {
                            existingNotification.preferences.splice(
                                existingPreferenceIndex,
                                1,
                                changedPreference
                            );
                        }
                    }
                }
            }
        },
        async getNotificationPreferences () {
            if (this.notificationPreferencesFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setNotificationPreferencesFetchState(FetchState.Loading);
            try {
                const response = await getNotificationPreferences();
                this.setNotificationPreferencesFetchState(FetchState.Loaded);
                this.setNotificationPreferences(response.data);
            } catch (err) {
                this.setNotificationPreferencesFetchState(FetchState.Failed);
                storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
            }
        },
        async savePaperlessNotificationPreferences () {
            this.updatePaperlessCommunicationPreferences();
            this.saveNotificationPreferences();
        },
        async saveNotificationPreferences () {
            this.AddChangedNotificationPreferences();

            if (this.updatedPreferences.length === 0) {
                return;
            }
            const request: NotificationPreferenceUpdateRequest = {
                notificationPreferences: this.updatedPreferences
            };
            await this.updateNotificationPreferences(request);
            this.updatedPreferences = [];
        },
        async updateNotificationPreferences (request: NotificationPreferenceUpdateRequest) {
            if (this.notificationPreferencesFetchState !== FetchState.Loaded) {
                return Promise.resolve();
            }
            const notifierStore = useNotifierStore();
            try {
                this.hasSavedNotificationPreferences = false;
                const response = await updateNotificationPreferences(request);
                this.hasSavedNotificationPreferences = true;
                this.setNotificationPreferences(response);
                notifierStore.setSuccess("Communication preferences updated successfully.", notifierStore.shortTimeout);
            } catch (err) {
                const errorMessage = "Sorry, we are unable to update your communication preferences. Please try again later.";

                storeHelper.handleError({
                    error: err,
                    rethrow: false,
                    timeout: 5000,
                    overrideNotificationMessage: errorMessage
                });
                this.setNotificationPreferencesFetchState(FetchState.Failed);
                this.notificationPreferences = _.cloneDeep(this.notificationPreferencesSnapshot);
                window.setTimeout(() => {
                    this.setNotificationPreferencesFetchState(FetchState.Loaded);
                }, 0);
            }
        },
    }
});